import maModal from '@/components/modal'
import maForm from '@/components/form'
import maButton from '@/components/button-blue'
import maNotifications from '@/components/notifications'
import debounce from '@/utils/debounce'
import maSpinner from '@/components/spinner'
import { mapState, mapActions, mapMutations } from 'vuex'
import { USER_MODULE_NAME } from '@/modules/User/store/types'
import {
  WARRANTY_MODULE_NAME,
  WARRANTY_GET_FORMS,
  WARRANTY_CLEAR_FORMS,
  WARRANTY_CLEAR_REPORT_MURS,
  WARRANTY_UPDATE_MURS_TOTAL_COUNT
} from '@/store/types'
import flowMixin from '@/modules/Flow/mixins/flowMixin'
import {
  AUTRES_GEN,
  AUTRES_VALUE,
  GLASS_SURFACE_LEN,
  GLASS_SURFACE_WID,
  ROOM_SURFACE
} from '@/constants'

export default {
  name: 'multidamageForm',

  mixins: [flowMixin],

  props: {
    damageReport: {
      required: true,
      type: Object
    },
    pieceId: {
      required: true,
      type: Number | null
    },
    damagedPart: {
      required: true,
      type: Object | null
    },
    pieceSurface: {
      required: true,
      type: Number | null
    },
    editIndex: {
      required: false,
      type: Number | null,
      default: null
    }
  },

  data: function () {
    return {
      formData: null,
      damageIndex: null,
      imgListEnsemble: [],
      imgListPres: [],
      isDisabledBtn: null,
      spinner: true,
      inputData: null,
      loading: true,
      avoidUpdateFormFields: [
        AUTRES_GEN,
        AUTRES_VALUE,
        GLASS_SURFACE_LEN,
        GLASS_SURFACE_WID
      ]
    }
  },

  components: {
    maModal,
    maForm,
    maButton,
    maNotifications,
    maSpinner
  },

  computed: {
    ...mapState(WARRANTY_MODULE_NAME, {
      stateWarrantyForms: 'forms',
      stateWarrantyCounterMurs: 'counterMurs'
    }),

    ...mapState(USER_MODULE_NAME, {
      stateUserType: 'userType'
    }),

    report() {
      return {
        ...this.damageReport,
        SUPPORT: this.damagedPart.key
      }
    }
  },

  methods: {
    ...mapActions(WARRANTY_MODULE_NAME, {
      actWarrantyGetForms: WARRANTY_GET_FORMS
    }),

    ...mapMutations(WARRANTY_MODULE_NAME, {
      mutWarrantyClearForms: WARRANTY_CLEAR_FORMS,
      mutWarrantyClearReportMurs: WARRANTY_CLEAR_REPORT_MURS,
      mutStateWarrantyUpdateMursTotalCount: WARRANTY_UPDATE_MURS_TOTAL_COUNT
    }),

    //When modal shows in the screen
    showModal() {
      this.loading = true
      this.spinner = false

      if (this.editIndex === null) {
        this.getForm(this.report)
      } else {
        this.damageIndex = this.editIndex
        const report = {
          ...this.report,
          ...this.damagedPart.partDamages[this.damageIndex].damages
        }

        if (this.damagedPart.key.indexOf('MUR') !== -1) {
          this.mutStateWarrantyUpdateMursTotalCount(
            this.damagedPart.partDamages[this.damageIndex].damages.QUANTITY
          )
        }

        if (this.damagedPart.partDamages[this.damageIndex].images.length > 0) {
          this.damagedPart.partDamages[this.damageIndex].images.forEach(
            (image) => {
              if (image.imageType === 'inputImgPres') {
                this.imgListPres.push(image)
              }
              if (image.imageType === 'inputImgEnsemble') {
                this.imgListEnsemble.push(image)
              }
            }
          )
        }
        this.getForm(report)
      }
      this.$refs.modalMultidamageForm.showModal = true
    },

    hideModal() {
      this.resetForms()
      this.$refs.modalMultidamageForm.showModal = false
    },

    getForm(report) {
      this.formData = null
      this.actWarrantyGetForms({ report, warrantyId: this.getWarrantyId }).then(
        (resp) => {
          if (resp.formsUpdated) {
            this.formData = this.stateWarrantyForms
            setTimeout(() => {
              this.filterForm()
              this.setSelectedValueInputs()
              this.validatedInputsText()
              setTimeout(() => {
                this.spinner = false
              }, 1000)
              this.loading = false
            })
          }
        }
      )
    },

    handleOnChange(input) {
      if (!this.isUpdateFormRequired(input.inputId)) {
        this.updateFormManually({
          id: input.inputId,
          value: input.inputVal
        })
        this.setSelectedValueInputs()
      }
    },

    /**
     * Sets active input value and calls updateForm method
     */
    handleOnFormChange: debounce(function (input = null) {
      if (this.isUpdateFormRequired(input.inputId)) {
        this.spinner = true
        this.mutWarrantyClearForms()
        this.disabledInputs(true)

        const report = {
          ...this.report,
          ...this.damagedPart.partDamages[this.damageIndex].damages
        }
        report[input.inputId] = input.inputVal

        if (this.damagedPart && this.damagedPart.key.indexOf('MUR') !== -1) {
          report.QUANTITY = this.stateWarrantyCounterMurs || 1
        }

        this.getForm(report)
      }
    }, 100),

    filterForm() {
      this.formData.forEach((form) => {
        form.form.regions[0].inputs = form.form.regions[0].inputs.filter(
          (input) => {
            return (
              input.name !== 'ASSETS' &&
              input.name !== 'PIECE' &&
              input.name !== 'SUPPORT' &&
              input.type !== 'hidden'
            )
          }
        )
        return form
      })
    },

    /**
     * Sets the defauld values for each form - the input.selected values that come from the forms api
     */
    setSelectedValueInputs() {
      const partDamage = {
        damages: {}
      }
      let labelSaved = false
      this.formData.forEach((formCategory) => {
        formCategory.form.regions.forEach((region) => {
          region.inputs.forEach((input) => {
            if (input.selectedValue) {
              // Save main data to display label damage part on damagePartList
              if (!labelSaved) {
                partDamage.type = input.name
                partDamage.value = input.selectedValue
                partDamage.literal = input.options.find(
                  (option) => option.key === input.selectedValue
                ).literal

                labelSaved = true
              }

              // Set global piece surface value
              // ATTENTION!!! ROOM_SURFACE and SURFACE_EVAL_PIECE are hidden by css
              if (input.name === ROOM_SURFACE) {
                partDamage.damages[input.name] = this.pieceSurface
              } else if (input.name === 'SURFACE_EVAL') {
                partDamage.damages[input.name] = 'SURFACE_EVAL_PIECE'
              } else {
                partDamage.damages[input.name] = input.selectedValue
              }
            }
          })
        })

        // Set asset damages
        partDamage.asset_damages = formCategory.form.asset_damages
      })

      if (this.damagedPart && this.damagedPart.key.indexOf('MUR') !== -1) {
        const { key, value } = this.updateQtyMurs()
        partDamage.damages[key] = value
      }

      if (this.damageIndex === null) {
        this.damagedPart.partDamages != null
          ? this.damagedPart.partDamages.push({ ...partDamage })
          : (this.damagedPart.partDamages = [{ ...partDamage }])
        this.damageIndex = this.damagedPart.partDamages.length - 1
      } else {
        if (this.damagedPart.partDamages[this.damageIndex] === null) {
          this.damagedPart.partDamages[this.damageIndex] = {}
        }
        this.damagedPart.partDamages[this.damageIndex] = { ...partDamage }
      }

      this.disabledInputs(false)
    },

    disabledInputs(disabled) {
      const formInputs = document.querySelectorAll('.modal__main input')
      formInputs.forEach((input) => {
        input.disabled = disabled
      })
    },

    /**
     * Updates the murs quantity
     */
    updateQtyMurs() {
      return {
        key: 'QUANTITY',
        value: this.stateWarrantyCounterMurs || 1
      }
    },

    /**
     * If the value type is not and object and it finds a ','
     * in the value it replaces it by '.' otherwise it returns the value
     */
    formattedValue(value) {
      return typeof value !== 'object' && value.indexOf(',') !== -1
        ? value.replace(',', '.')
        : value
    },

    /**
     * Pushes the image in the imgListEnsemble array
     */
    handleOnImgChangeEnsemble(event) {
      const dataImg = event.fileData ? event.fileData : event
      this.imgListEnsemble.push(dataImg)
      this.handleEnableButton()
    },

    /**
     * Pushes the image in the imgListPres array
     */
    handleOnImgChangePres(event) {
      const dataImg = event.fileData ? event.fileData : event
      this.imgListPres.push(dataImg)
      this.handleEnableButton()
    },

    /**
     * Checks if required images are uploaded
     */
    checkImages() {
      if (
        !this.addDamagesHasImages ||
        Object.keys(this.imgListEnsemble)[0] === '0' ||
        Object.keys(this.imgListPres)[0] === '0'
      )
        return true
      return this.imgListPres.length > 0 && this.imgListEnsemble.length > 0
    },

    validatedInputsText() {
      let disabled = false
      this.formData[0].form.regions.forEach((element) => {
        element.inputs.forEach((input) => {
          if (
            input.type.includes('text') &&
            input.name !== ROOM_SURFACE &&
            input.name !== AUTRES_VALUE
          ) {
            input.selectedValue = input.selectedValue.toString()
            if (this.isNumber(input.selectedValue)) {
              input.selectedValue = this.formattedValue(input.selectedValue)
              input.selectedValue = Number.parseFloat(input.selectedValue)
            }
            if (input.selectedValue <= 0 || input.selectedValue === '') {
              disabled = true
            }
          }
        })
      })
      return disabled
    },

    handleEnableButton() {
      if (!this.validatedInputsText()) {
        if (this.checkImages()) {
          this.isDisabledBtn = false
        } else {
          this.isDisabledBtn = true
        }
      } else {
        this.isDisabledBtn = true
      }
    },

    /**
     * Checks if the form is valid and sends the updated information to the parent component
     */
    handleOnAddDamagePart() {
      if (!this.validatedInputsText()) {
        if (this.checkImages()) {
          this.damagedPart.partDamages[this.damageIndex].images = [
            ...this.imgListEnsemble,
            ...this.imgListPres
          ]

          // Update murs
          if (this.damagedPart.key.indexOf('MUR') !== -1) {
            this.damagedPart.partDamages[this.damageIndex].damages.QUANTITY =
              this.stateWarrantyCounterMurs
          }

          this.$emit('form:partDamageAdded', {
            pieceType: this.damageReport.PIECE,
            pieceId: this.pieceId,
            damagedPart: this.damagedPart
          })
          this.hideModal()
        } else {
          this.throwNotification(
            this.$t('claim.details.errorImages'),
            this.$t('claim.details.errorImages'),
            'KO'
          )

          this.sendSentryEvent(
            'Error in multidamage call',
            this.handleOnAddDamagePart.name,
            {
              imagesMultiFormData:
                this.damagedPart.partDamages[this.damageIndex].images
            }
          )
        }
      } else {
        window.scrollTo(0, 0)
        this.throwNotification(
          this.$t('claim.details.errorMsgGeneral'),
          this.$t('claim.details.errorMsgGeneral'),
          'KO'
        )
      }
    },

    resetForms() {
      this.formData = null
      this.damageIndex = null
      this.imgListEnsemble = []
      this.imgListPres = []
      this.mutWarrantyClearForms()
      this.mutWarrantyClearReportMurs()
    },

    /**
     * Sets the focus when an input is modified
     */
    handleInputFocus() {
      const focusedInput = document.querySelector('input:focus')
      if (document.querySelector('input:focus') !== null) {
        this.activeInput = focusedInput
      }
    },

    updateFormManually(data) {
      this.formData.forEach((formData) => {
        const inputs = formData.form.regions[0].inputs
        inputs.forEach((input) => {
          if (
            this.avoidUpdateFormFields.includes(input.name) &&
            input.name === data.id
          ) {
            input.selectedValue = data.value
          }
        })
      })
    },

    isUpdateFormRequired(inputId) {
      return !this.avoidUpdateFormFields.includes(inputId)
    }
  }
}
